import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Button, 
  ButtonGroup,
  Typography, 
  Paper, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody,
  useTheme,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  MenuItem
} from '@mui/material';
import { 
  Add as AddIcon,
  TableChart as TableIcon,
  Receipt as InvoiceIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { chargingSessionService, companiesService, whitelistService } from '../services/api';
import { AppUser, ChargingSessionData, Company, WhitelistEntry } from '../types';
import { formatDateForAPIFrom, formatDateForAPITo } from '../utils/dateUtils';
import * as XLSX from 'xlsx';

// Billing Invoice interface
interface BillingInvoice {
  id: string;
  company: string;
  dateStart: string;
  dateEnd: string;
  energyDelivered: number;
  price: number;
  totalPrice: number;
}

const normalizeKey = (value?: string | null): string => (value ?? '').trim().toLowerCase();

// Same key the backend uses for adhoc_company_price.company_key.
const adhocCompanyKey = (value?: string | null): string =>
  (value ?? '').trim().split(/\s+/).join(' ').toLowerCase();

const isAnnetCompany = (company: Company) =>
  Boolean(company.project_id) || /^annet(\s|$)/i.test(company.company_name.trim());

const annetPriceForProject = (companies: Company[], projectId: string): number | null => {
  const forProject = companies.find((company) => company.project_id === projectId);
  const namedAnnet = companies.find((company) => /^annet(\s|$)/i.test(company.company_name.trim()));
  const price = Number((forProject ?? namedAnnet)?.charging_price);
  return Number.isFinite(price) ? price : null;
};

const toIdentifierList = (value?: string[] | string | null): string[] => {
  if (!value) return [];
  return (Array.isArray(value) ? value : [value]).map((item) => item.trim()).filter(Boolean);
};

const getCompanyRfids = (company: Company): string[] =>
  toIdentifierList(company.identifiers?.rfid || company.rfid);

const getCompanyEvccIds = (company: Company): string[] =>
  toIdentifierList(company.identifiers?.evcc_id || company.evcc_id);

const buildBillingLookups = (companies: Company[], whitelist: WhitelistEntry[] = []) => {
  const companyByName = new Map<string, Company>();
  const companyById = new Map<string, Company>();
  const companyByRfid = new Map<string, Company>();
  const companyByEvcc = new Map<string, Company>();
  const appUserByRfid = new Map<string, { company: Company; appUser: AppUser }>();
  const appUserByName = new Map<string, { company: Company; appUser: AppUser }>();

  companies.forEach((company) => {
    const companyNameKey = normalizeKey(company.company_name);
    if (companyNameKey) {
      companyByName.set(companyNameKey, company);
    }
    if (company.id != null) {
      companyById.set(String(company.id), company);
    }

    getCompanyRfids(company).forEach((rfid) => {
      const key = normalizeKey(rfid);
      if (key) companyByRfid.set(key, company);
    });

    getCompanyEvccIds(company).forEach((evccId) => {
      const key = normalizeKey(evccId);
      if (key) companyByEvcc.set(key, company);
    });

    (company.app_users ?? []).forEach((appUser) => {
      const rfidKey = normalizeKey(appUser.rfid);
      const nameKey = normalizeKey(appUser.name);
      if (rfidKey) appUserByRfid.set(rfidKey, { company, appUser });
      if (nameKey) appUserByName.set(nameKey, { company, appUser });
    });
  });

  // Cards keep their company id when the company is renamed, so a session
  // stored under the old name still bills to the current company.
  whitelist.forEach((entry) => {
    if (entry.company_id == null) return;
    const company = companyById.get(String(entry.company_id));
    const rfidKey = normalizeKey(entry.rfid);
    if (!company || !rfidKey || isAnnetCompany(company)) return;
    if (!companyByRfid.has(rfidKey)) companyByRfid.set(rfidKey, company);
    if (!appUserByRfid.has(rfidKey)) {
      appUserByRfid.set(rfidKey, {
        company,
        appUser: { rfid: entry.rfid, name: entry.user_name || '' },
      });
    }
  });

  return { companyByName, companyByRfid, companyByEvcc, appUserByRfid, appUserByName };
};

const resolveSessionCompany = (
  session: ChargingSessionData,
  lookups: ReturnType<typeof buildBillingLookups>
): Company | null => {
  const rfidKey = normalizeKey(session.rfid);
  const evccKey = normalizeKey(session.evcc_id);
  const customerKey = normalizeKey(session.customer);

  const appUserMatch =
    (rfidKey ? lookups.appUserByRfid.get(rfidKey) : undefined) ||
    (customerKey ? lookups.appUserByName.get(customerKey) : undefined);

  if (appUserMatch) {
    return appUserMatch.company;
  }

  return (
    (rfidKey ? lookups.companyByRfid.get(rfidKey) : undefined) ||
    (evccKey ? lookups.companyByEvcc.get(evccKey) : undefined) ||
    (customerKey ? lookups.companyByName.get(customerKey) : undefined) ||
    null
  );
};

const isAnnetName = (name: string) => /^annet(\s|$)/i.test(name.trim());

const sessionCompanyName = (
  session: ChargingSessionData,
  lookups: ReturnType<typeof buildBillingLookups>
): string => {
  const company = resolveSessionCompany(session, lookups);
  if (company && !isAnnetCompany(company)) return company.company_name;

  const customer = (session.customer || '').trim();
  if (customer && !isAnnetName(customer)) return customer;

  return 'Annet';
};

// Invoice Document interface
interface InvoiceDocument {
  id: string;
  name: string;
  type: string;
  size: string;
  uploadedDate: string;
  company: string;
  period: string;
}

const getCurrentMonthKey = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthDateRange = (monthKey: string): { from: Date; to: Date } => {
  const [year, month] = monthKey.split('-').map(Number);
  return {
    from: new Date(year, month - 1, 1),
    to: new Date(year, month, 0),
  };
};

const shiftMonthKey = (monthKey: string, offset: number): string => {
  const [year, month] = monthKey.split('-').map(Number);
  const shifted = new Date(year, month - 1 + offset, 1);
  return `${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, '0')}`;
};

const BillingInvoices: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { projectId } = useParams<{ projectId: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // View state - 'table' or 'invoices'
  const [currentView, setCurrentView] = useState<'table' | 'invoices'>('table');
  

  // Data state
  const [billingInvoicesData, setBillingInvoicesData] = useState<BillingInvoice[]>([]);
  const [chargingSessionsData, setChargingSessionsData] = useState<ChargingSessionData[]>([]);
  const [companiesData, setCompaniesData] = useState<Company[]>([]);
  const [whitelistData, setWhitelistData] = useState<WhitelistEntry[]>([]);
  const [invoiceDocumentsData, setInvoiceDocumentsData] = useState<InvoiceDocument[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonthKey);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);


  // Fetch and calculate billing data for the selected month
  const fetchBillingData = useCallback(async () => {
    if (!user?.orgId) {
      setError('No organization ID available');
      return;
    }

    if (!projectId) {
      setError('No project ID available');
      return;
    }

    if (!selectedMonth) {
      setError('No month selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [companiesResponse, whitelistResponse, adhocResponse] = await Promise.all([
        companiesService.getCompanies(user.orgId),
        whitelistService.getProjectWhitelist(projectId, user.orgId).catch(() => null),
        companiesService.getAdhocCompanies(user.orgId, projectId).catch((err) => {
          console.error('Error fetching adhoc company prices:', err);
          return null;
        }),
      ]);
      if (!companiesResponse.success) {
        throw new Error('Failed to fetch companies data');
      }

      const companies = companiesResponse.data.companies;
      const whitelistEntries = whitelistResponse?.data?.entries || [];
      setCompaniesData(companies);
      setWhitelistData(whitelistEntries);
      const billableCompanies = companies.filter((company) => !isAnnetCompany(company));
      const lookups = buildBillingLookups(billableCompanies, whitelistEntries);
      const annetPrice = annetPriceForProject(companies, projectId);
      const adhocPriceByKey = new Map<string, number>();
      (adhocResponse?.data?.projects ?? [])
        .filter((project) => project.project_id === projectId)
        .forEach((project) => project.companies.forEach((adhoc) => {
          if (adhoc.has_custom_price) {
            adhocPriceByKey.set(adhoc.company_key || adhocCompanyKey(adhoc.company_name), Number(adhoc.charging_price));
          }
        }));

      const { from, to } = getMonthDateRange(selectedMonth);
      const fromDate = formatDateForAPIFrom(from);
      const toDate = formatDateForAPITo(to);

      // Fetch charging sessions for the selected month only
      const chargingSessionsResponse = await chargingSessionService.getProjectChargingSessions(
        user.orgId,
        projectId,
        fromDate,
        toDate
      );

      if (!chargingSessionsResponse.success) {
        throw new Error('Failed to fetch charging sessions data');
      }

      const sessions = chargingSessionsResponse.data.sessions || chargingSessionsResponse.data.data || [];
      console.log(`Fetched ${sessions.length} sessions for project ${projectId} (${selectedMonth})`);
      setChargingSessionsData(sessions);
      
      // Sum energy for each company that appears on a charging session.
      const energyByCompany = new Map<string, number>();

      sessions.forEach((session: ChargingSessionData) => {
        if (!session.energy || !session.date) return;

        const companyName = sessionCompanyName(session, lookups);
        energyByCompany.set(companyName, (energyByCompany.get(companyName) || 0) + session.energy);
      });

      // Company-page price for users on a real company; the adhoc company's own
      // price if one is set on the Companies page; Annet price for everyone else.
      const periodStart = from;
      const periodEnd = to;
      const invoices: BillingInvoice[] = [];
      let invoiceId = 1;

      energyByCompany.forEach((energy, companyName) => {
        const company = lookups.companyByName.get(normalizeKey(companyName));
        const companyPrice = Number(company?.charging_price);
        const adhocPrice = adhocPriceByKey.get(adhocCompanyKey(companyName));
        let price = annetPrice ?? 0;
        if (company && !isAnnetCompany(company) && Number.isFinite(companyPrice)) {
          price = companyPrice;
        } else if (adhocPrice !== undefined && Number.isFinite(adhocPrice)) {
          price = adhocPrice;
        }

        invoices.push({
          id: String(invoiceId++),
          company: companyName,
          dateStart: formatDate(periodStart),
          dateEnd: formatDate(periodEnd),
          energyDelivered: energy,
          price,
          totalPrice: energy * price
        });
      });

      invoices.sort((a, b) => a.company.localeCompare(b.company));

      setBillingInvoicesData(invoices);
      setInvoiceDocumentsData([]); // No mock data - empty array
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch billing data';
      setError(errorMessage);
      console.error('Error fetching billing data:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.orgId, projectId, selectedMonth]);

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const newDocument: InvoiceDocument = {
        id: Date.now().toString(), // Simple ID generation
        name: file.name,
        type: getFileTypeDisplay(file),
        size: formatFileSize(file.size),
        uploadedDate: new Date().toLocaleDateString('en-GB'),
        company: 'Unknown Company', // Default value, could be made editable
        period: new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      };
      
      setInvoiceDocumentsData(prev => [...prev, newDocument]);
    }
  };

  // Get user-friendly file type display
  const getFileTypeDisplay = (file: File): string => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    // Check file extension first (more reliable)
    if (extension) {
      switch (extension) {
        case 'pdf': return 'PDF';
        case 'doc': return 'DOC';
        case 'docx': return 'DOCX';
        case 'txt': return 'TXT';
        case 'jpg':
        case 'jpeg': return 'JPG';
        case 'png': return 'PNG';
        case 'xls': return 'XLS';
        case 'xlsx': return 'XLSX';
        case 'ppt': return 'PPT';
        case 'pptx': return 'PPTX';
        default: return extension.toUpperCase();
      }
    }
    
    // Fallback to MIME type parsing
    const mimeType = file.type.split('/')[1];
    return mimeType ? mimeType.toUpperCase() : 'FILE';
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Trigger file input
  const handleAddInvoice = () => {
    fileInputRef.current?.click();
  };

  // Load data when component mounts
  useEffect(() => {
    if (user?.orgId && projectId) {
      fetchBillingData();
    }
  }, [user?.orgId, projectId, fetchBillingData]);

  const companyOptions = Array.from(new Set(billingInvoicesData.map((invoice) => invoice.company))).sort((a, b) =>
    a.localeCompare(b)
  );

  // Data is already scoped to selectedMonth from the API; filter by company when set
  const filteredData = billingInvoicesData.filter((invoice) =>
    selectedCompany ? invoice.company === selectedCompany : true
  );

  const totalEnergyDelivered = filteredData.reduce((sum, invoice) => sum + invoice.energyDelivered, 0);
  const totalPriceSum = filteredData.reduce((sum, invoice) => sum + invoice.totalPrice, 0);

  const formatEnergy = (energy: number) => {
    return `${energy.toFixed(1)} kWh`;
  };

  const formatPrice = (price: number) => {
    return `${price.toFixed(2)} kr/kWh`;
  };

  const formatTotalPrice = (totalPrice: number) => {
    return `${totalPrice.toFixed(2)} kr`;
  };

  const handleExportToExcel = () => {
    if (filteredData.length === 0) return;

    const rows = filteredData.map((invoice) => ({
      Company: invoice.company,
      'Period Start': invoice.dateStart,
      'Period End': invoice.dateEnd,
      'Energy Delivered (kWh)': Number(invoice.energyDelivered.toFixed(1)),
      'Price (kr/kWh)': Number(invoice.price.toFixed(2)),
      'Total Price (kr)': Number(invoice.totalPrice.toFixed(2))
    }));

    const totalEnergy = filteredData.reduce((sum, invoice) => sum + invoice.energyDelivered, 0);
    const totalPrice = filteredData.reduce((sum, invoice) => sum + invoice.totalPrice, 0);

    rows.push({
      Company: 'TOTAL',
      'Period Start': '',
      'Period End': '',
      'Energy Delivered (kWh)': Number(totalEnergy.toFixed(1)),
      'Price (kr/kWh)': '',
      'Total Price (kr)': Number(totalPrice.toFixed(2))
    } as unknown as (typeof rows)[number]);

    const lookups = buildBillingLookups(
      companiesData.filter((company) => !isAnnetCompany(company)),
      whitelistData
    );
    const selectedCompanyNormalized = normalizeKey(selectedCompany);

    const filteredChargingSessions = chargingSessionsData.filter((session) => {
      if (!selectedCompany) return true;
      return normalizeKey(sessionCompanyName(session, lookups)) === selectedCompanyNormalized;
    });

    const sessionRows = filteredChargingSessions
      .map((session) => {
        return {
          'Session ID': session.id,
          Date: session.date,
          Company: sessionCompanyName(session, lookups),
          'Unit Name': session.unit_name,
          Outlet: session.outlet ?? '',
          RFID: session.rfid,
          'Start Time': session.start_time ?? '',
          'Duration (min)': session.duration ?? '',
          'Energy (kWh)': session.energy ?? '',
          'Avg Power (kW)': session.avg_power ?? '',
          'Max Power (kW)': session.max_power ?? '',
          'Start SoC (%)': session.start_soc ?? '',
          'End SoC (%)': session.end_soc ?? '',
          'EVCC ID': session.evcc_id ?? ''
        };
      });

    const metadataRows = [
      ['Generated At', new Date().toLocaleString()],
      ['Month Filter', selectedMonth],
      ['Company Filter', selectedCompany || 'All companies'],
      []
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(metadataRows);
    XLSX.utils.sheet_add_json(worksheet, rows, { origin: 'A5', skipHeader: false });

    const sessionsWorksheet = XLSX.utils.aoa_to_sheet(metadataRows);
    XLSX.utils.sheet_add_json(sessionsWorksheet, sessionRows, { origin: 'A5', skipHeader: false });

    const autoSizeColumns = (sheet: XLSX.WorkSheet) => {
      const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
      const colWidths: { wch: number }[] = [];

      for (let col = range.s.c; col <= range.e.c; col += 1) {
        let maxLength = 10;
        for (let row = range.s.r; row <= range.e.r; row += 1) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          const cell = sheet[cellAddress];
          if (!cell || cell.v === undefined || cell.v === null) continue;
          const cellText = String(cell.v);
          maxLength = Math.max(maxLength, cellText.length + 2);
        }
        colWidths.push({ wch: maxLength });
      }

      sheet['!cols'] = colWidths;
    };

    const applyTotalRowStyle = (sheet: XLSX.WorkSheet, dataRows: number) => {
      const totalRowNumber = 5 + dataRows; // Header at row 5, data starts row 6
      const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
      for (let col = range.s.c; col <= range.e.c; col += 1) {
        const cellAddress = XLSX.utils.encode_cell({ r: totalRowNumber - 1, c: col });
        const cell = sheet[cellAddress];
        if (!cell) continue;
        cell.s = {
          font: { bold: true, color: { rgb: '1B5E20' } },
          fill: { fgColor: { rgb: 'E8F5E9' } }
        };
      }
    };

    autoSizeColumns(worksheet);
    autoSizeColumns(sessionsWorksheet);
    applyTotalRowStyle(worksheet, rows.length);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sessionsWorksheet, 'chargingSession data');
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Billing Summary');

    const fileMonth = selectedMonth;
    const fileCompany = selectedCompany ? selectedCompany.replace(/\s+/g, '-').toLowerCase() : 'all-companies';
    XLSX.writeFile(workbook, `billing-invoices-${fileMonth}-${fileCompany}.xlsx`, { cellStyles: true });
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: theme.palette.background.default, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3}}>
        <Typography variant="h4" sx={{fontWeight: 600, color: theme.palette.text.primary }}>
          Billing / Invoices
        </Typography>
      </Box>
      <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 4}}>
        This is the Billing / Invoices page. Here you can view and manage billing information and invoices.
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading billing data...
          </Typography>
        </Box>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
      />

      {/* Main Content - only show when not loading */}
      {!loading && (
        <>
          {/* View Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 3 }}>
        <ButtonGroup variant="outlined" aria-label="view selection">
          <Button
            startIcon={<TableIcon />}
            onClick={() => setCurrentView('table')}
            variant={currentView === 'table' ? 'contained' : 'outlined'}
            sx={{
              backgroundColor: currentView === 'table' ? theme.palette.primary.light : 'transparent',
              color: currentView === 'table' ? 'white' : theme.palette.primary.light,
              borderColor: theme.palette.primary.light,
              '&:hover': {
                backgroundColor: currentView === 'table' ? theme.palette.primary.main : 'rgba(79, 120, 92, 0.04)',
                borderColor: theme.palette.primary.light,
              },
            }}
          >
            Table
          </Button>
          <Button
            startIcon={<InvoiceIcon />}
            onClick={() => setCurrentView('invoices')}
            variant={currentView === 'invoices' ? 'contained' : 'outlined'}
            sx={{
              backgroundColor: currentView === 'invoices' ? theme.palette.primary.light : 'transparent',
              color: currentView === 'invoices' ? 'white' : theme.palette.primary.light,
              borderColor: theme.palette.primary.light,
              '&:hover': {
                backgroundColor: currentView === 'invoices' ? theme.palette.primary.main : 'rgba(79, 120, 92, 0.04)',
                borderColor: theme.palette.primary.light,
              },
            }}
          >
            Invoices
          </Button>
        </ButtonGroup>
      </Box>

      {/* Month Filter */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size="small"
            aria-label="Previous month"
            onClick={() => setSelectedMonth((month) => shiftMonthKey(month, -1))}
            sx={{ color: theme.palette.primary.light }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <TextField
            label="Month"
            type="month"
            size="small"
            value={selectedMonth}
            onChange={(event) => {
              if (event.target.value) {
                setSelectedMonth(event.target.value);
              }
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
          <IconButton
            size="small"
            aria-label="Next month"
            onClick={() => setSelectedMonth((month) => shiftMonthKey(month, 1))}
            sx={{ color: theme.palette.primary.light }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <TextField
          select
          label="Company"
          size="small"
          value={selectedCompany}
          onChange={(event) => setSelectedCompany(event.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All companies</MenuItem>
          {companyOptions.map((company) => (
            <MenuItem key={company} value={company}>
              {company}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          onClick={() => {
            setSelectedMonth(getCurrentMonthKey());
            setSelectedCompany('');
          }}
          disabled={selectedMonth === getCurrentMonthKey() && !selectedCompany}
          sx={{ borderColor: theme.palette.primary.light, color: theme.palette.primary.light }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExportToExcel}
          disabled={filteredData.length === 0}
          sx={{
            backgroundColor: theme.palette.primary.light,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          Export Excel
        </Button>
      </Box>


      {/* Conditional Content Based on View */}
      {currentView === 'table' ? (
        <>
          {/* Summary */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 2 }}>
            <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
              <Box component="span" sx={{ fontWeight: 600 }}>Energy delivered:</Box> {formatEnergy(totalEnergyDelivered)}
            </Typography>
            <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
              <Box component="span" sx={{ fontWeight: 600 }}>Total price:</Box> {formatTotalPrice(totalPriceSum)}
            </Typography>
          </Box>

          {/* Billing Invoices Table */}
          <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Period Start</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Period End</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Energy Delivered</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Total Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((invoice) => (
                  <TableRow key={invoice.id} sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        {invoice.company}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {invoice.dateStart}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {invoice.dateEnd}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {formatPrice(invoice.price)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                        {formatEnergy(invoice.energyDelivered)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                        {formatTotalPrice(invoice.totalPrice)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* No results message for table view */}
          {filteredData.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No billing invoices found for the selected month.
              </Typography>
            </Box>
          )}
        </>
      ) : (
        /* Invoices View - Card-based layout */
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontFamily: 'Montserrat',
                fontSize: '14px',
                lineHeight: 1.43
              }}
            >

            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddInvoice}
              sx={{
                backgroundColor: theme.palette.primary.light,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            >
              ADD INVOICE
            </Button>
          </Box>
          
          {invoiceDocumentsData.length > 0 ? (
            invoiceDocumentsData.map((document, index) => (
              <Box 
                key={document.id}
                sx={{ 
                  border: '1px solid rgba(0,0,0,0.23)',
                  borderRadius: '4px',
                  mb: 2, // 16px spacing between elements
                  display: 'flex',
                  alignItems: 'stretch',
                  minHeight: '60px'
                }}
              >
                {/* Document Details */}
                <Box sx={{ 
                  flex: 1, 
                  p: 2, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 0.25 
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.palette.primary.light,
                      textDecoration: 'underline',
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      lineHeight: 1.43,
                      cursor: 'pointer',
                      '&:hover': {
                        color: theme.palette.primary.main
                      }
                    }}
                  >
                    {document.name}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      fontFamily: 'Montserrat',
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: 1.66,
                      letterSpacing: '0.4px'
                    }}
                  >
                    {document.type}, {document.size}, uploaded {document.uploadedDate}
                  </Typography>
                </Box>
                
                {/* Actions */}
                <Box sx={{ 
                  borderLeft: '1px solid rgba(0,0,0,0.23)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '60px'
                }}>
                  {/* Download Button */}
                  <IconButton 
                    size="small"
                    sx={{ 
                      p: 0.5,
                      borderRadius: '48px',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)'
                      }
                    }}
                  >
                    <DownloadIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                  </IconButton>
                  
                  {/* Divider */}
                  <Divider sx={{ width: '100%', my: 0.5 }} />
                  
                  {/* View Button */}
                  <IconButton 
                    size="small"
                    sx={{ 
                      p: 0.5,
                      borderRadius: '48px',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)'
                      }
                    }}
                  >
                    <ViewIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                  </IconButton>
                </Box>
              </Box>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No invoice documents found. Upload invoices using the "ADD INVOICE" button above.
              </Typography>
            </Box>
          )}
        </Box>
      )}
        </>
      )}
    </Box>
  );
};

export default BillingInvoices;
